import type { CityConfig } from "@/pages/city-template/types";

export const newHavenConfig: CityConfig = {
  city: "New Haven",
  state: "Connecticut",
  stateAbbr: "CT",
  slug: "new-haven",
  route: "/cities/new-haven",
  title: "Expedited Passport Services in New Haven, CT | Fast Passport Center",
  metaDescription:
    "Get your passport fast in New Haven, CT. Fast Passport Center offers 24-hour to 10-day expedited passport processing. Registered State Department courier serving New Haven and all of Connecticut.",
  h1: "Expedited Passport Services in New Haven, CT",
  heroSubtitle:
    "Home to Yale University and a vibrant international community, New Haven travelers need fast, reliable passport service. Fast Passport Center delivers emergency and expedited passports as fast as 24 hours.",
  heroImageUrl:
    "https://readdy.ai/api/search-image?query=Scenic%20view%20of%20New%20Haven%20Connecticut%20downtown%20featuring%20historic%20architecture%20and%20Yale%20University%20campus%20buildings%2C%20warm%20golden%20hour%20lighting%2C%20green%20trees%20and%20New%20England%20charm%2C%20professional%20travel%20destination%20photography%2C%20clean%20modern%20composition%20with%20no%20text%20overlay%2C%20no%20people%20visible&width=1600&height=700&seq=ct-nh-hero-001&orientation=landscape",
  heroImageAlt: "Downtown New Haven Connecticut historic district at golden hour",
  geoPosition: "41.3083; -72.9279",
  geoPlacename: "New Haven, Connecticut, United States",
  lastModified: "2026-06-01",
  regionalAgency: {
    name: "New York Passport Agency",
    address: "376 Hudson Street, New York, NY 10014",
    distance: "~80 miles from New Haven",
  },
  nearbyCitiesZips: [
    { name: "New Haven", zips: "06501, 06502, 06503, 06504, 06505, 06506, 06507, 06508, 06509, 06510, 06511, 06513, 06515, 06517, 06518, 06519, 06520, 06521, 06525, 06530, 06531, 06532, 06533, 06534, 06535, 06536, 06537, 06538, 06540" },
    { name: "West Haven", zips: "06516" },
    { name: "East Haven", zips: "06512, 06513" },
    { name: "Hamden", zips: "06514, 06517, 06518" },
    { name: "North Haven", zips: "06473" },
    { name: "Milford", zips: "06460, 06461" },
    { name: "Orange", zips: "06477" },
    { name: "Wallingford", zips: "06492, 06493, 06494, 06495" },
    { name: "Branford", zips: "06405" },
    { name: "Guilford", zips: "06437" },
    { name: "Madison", zips: "06443" },
    { name: "Meriden", zips: "06450, 06451, 06454" },
    { name: "Cheshire", zips: "06408, 06410, 06411" },
    { name: "Bethany", zips: "06524" },
    { name: "Woodbridge", zips: "06525" },
    { name: "Ansonia", zips: "06401" },
    { name: "Derby", zips: "06418" },
    { name: "Seymour", zips: "06483" },
  ],
  benefits: [
    {
      icon: "ri-time-line",
      title: "As Fast as 24 Hours",
      desc: "Emergency processing available for New Haven travelers with urgent departure dates.",
    },
    {
      icon: "ri-shield-check-line",
      title: "Registered Courier",
      desc: "U.S. Department of State registered passport courier with reserved agency submission slots.",
    },
    {
      icon: "ri-customer-service-2-line",
      title: "Real Human Support",
      desc: "Dedicated agents guide you through every step — no chatbots, no automated runaround.",
    },
    {
      icon: "ri-map-pin-line",
      title: "Serving All of New Haven",
      desc: "Downtown, Yale campus, West Haven, East Rock, Wooster Square — we cover every New Haven neighborhood.",
    },
  ],
  testimonials: [
    {
      name: "Dr. Emily S.",
      location: "New Haven, CT",
      rating: 5,
      text: "As a Yale researcher with a last-minute conference in Berlin, I was panicking. Fast Passport Center got my passport renewed in 4 days. Their New Haven support was outstanding.",
      service: "Passport Renewal",
    },
    {
      name: "Michael R.",
      location: "West Haven, CT",
      rating: 5,
      text: "I needed an emergency passport for a family emergency overseas. Fast Passport Center processed it in under 48 hours. I cannot thank them enough.",
      service: "Emergency Passport",
    },
    {
      name: "Amanda L.",
      location: "Hamden, CT",
      rating: 5,
      text: "Applied for my children's passports through Fast Passport Center. They made the two-parent consent process simple and clear. Passports arrived in 5 days.",
      service: "Child Passport",
    },
  ],
  facilities: [
    {
      id: "nh-1",
      name: "New Haven Main Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "Woolsey Hall, 234 Church Street",
      city: "New Haven",
      state: "CT",
      zip: "06510",
      phone: "203-865-2395",
      hours: "Mon-Fri 9:00 AM - 4:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Downtown New Haven",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport acceptance services available by appointment. First-time applicants and those not eligible for mail-in renewal must apply in person.",
      icon: "ri-building-2-line",
    },
    {
      id: "nh-2",
      name: "New Haven City Clerk",
      type: "City Clerk / Passport Services",
      address: "165 Church Street",
      city: "New Haven",
      state: "CT",
      zip: "06510",
      phone: "203-946-7206",
      hours: "Mon-Fri 9:00 AM - 4:00 PM",
      distance: "Downtown New Haven",
      website: "https://www.newhavenct.gov/",
      websiteLabel: "City of New Haven",
      notes: "Provides passport application acceptance services. Appointment recommended for all passport services.",
      icon: "ri-government-line",
    },
    {
      id: "nh-3",
      name: "West Haven Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "231 Elm Street",
      city: "West Haven",
      state: "CT",
      zip: "06516",
      phone: "203-931-2251",
      hours: "Mon-Fri 9:00 AM - 4:00 PM",
      distance: "West Haven",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport photo services and application acceptance available. Appointment recommended for passport applications.",
      icon: "ri-file-shield-line",
    },
  ],
  faqs: [
    {
      q: "How fast can I get a passport in New Haven, CT?",
      a: "Fast Passport Center offers processing options from 24 hours to 10 business days. For emergency travel within 2 weeks, we recommend our 3-5 day or 24-hour emergency service. Standard processing through the post office takes 8-11 weeks.",
    },
    {
      q: "Do Yale students need special documentation for passports?",
      a: "Yale students follow the same passport application process as all U.S. citizens. If you're a student from another state, you can apply from New Haven using your Connecticut address or your permanent home address. Fast Passport Center helps students navigate the process regardless of their state of residence.",
    },
    {
      q: "Where can I get passport photos in New Haven?",
      a: "Passport photos are available at CVS Pharmacy, Walgreens, UPS Store, FedEx Office, and Walmart locations throughout New Haven and West Haven. We also offer photo review as part of our service to ensure your photos meet all State Department requirements before submission.",
    },
    {
      q: "Can I renew my passport by mail from New Haven?",
      a: "Yes, if your most recent passport was issued when you were 16 or older, within the last 15 years, and is undamaged. Mail renewal takes 8-11 weeks. For faster renewal, Fast Passport Center can expedite the process to as little as 3-5 days.",
    },
    {
      q: "What documents do I need for a first-time passport in New Haven?",
      a: "You'll need proof of U.S. citizenship (birth certificate or naturalization certificate), a government-issued photo ID, two passport photos, and payment for government fees. Fast Passport Center provides a complete checklist and reviews your documents before submission.",
    },
    {
      q: "Does Fast Passport Center have an office in New Haven?",
      a: "Fast Passport Center operates as a remote courier service — we do not have a physical office in New Haven. We serve New Haven and all of Connecticut through our secure document shipping and agency submission process. Our headquarters are in Washington, DC.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — New Haven", address: " various CVS locations in New Haven", hours: "Mon-Sun, hours vary by location" },
    { name: "Walgreens — New Haven", address: "various Walgreens locations in New Haven", hours: "Mon-Sun, hours vary by location" },
    { name: "UPS Store — New Haven", address: "various UPS Store locations in New Haven", hours: "Mon-Fri 9:00 AM - 7:00 PM, Sat 10:00 AM - 4:00 PM" },
    { name: "FedEx Office — New Haven", address: "various FedEx Office locations in New Haven", hours: "Mon-Fri 8:00 AM - 9:00 PM, Sat-Sun 10:00 AM - 6:00 PM" },
    { name: "Walmart — New Haven", address: "various Walmart locations in New Haven", hours: "Mon-Sun 7:00 AM - 10:00 PM" },
  ],
  dmvLocations: [
    {
      name: "CT DMV — Hamden",
      address: "2365 State Street, Hamden, CT 06517",
      hours: "Mon-Fri 8:00 AM - 4:00 PM",
      note: "Full DMV services for New Haven area residents. Appointments strongly recommended.",
      featured: true,
    },
    {
      name: "CT DMV — Milford",
      address: "70 West River Street, Milford, CT 06460",
      hours: "Mon-Fri 8:00 AM - 4:00 PM",
      note: "License renewals and REAL ID applications for shoreline residents.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "HVN",
      name: "Tweed-New Haven Airport",
      distance: "~4 miles from New Haven",
      address: "155 Burr Street, New Haven, CT 06512",
      note: "New Haven's regional airport with domestic flights and growing international connections. Convenient for New Haven and shoreline travelers.",
      flightUrl: "https://www.hvn.com/",
      flightLabel: "Tweed-New Haven Airport",
      featured: true,
    },
    {
      code: "BDL",
      name: "Bradley International Airport",
      distance: "~55 miles from New Haven",
      address: "Schoephoester Rd, Windsor Locks, CT 06096",
      note: "Connecticut's major airport with domestic and international flights. Accessible via I-91 north from New Haven.",
      flightUrl: "https://www.bradleyairport.com/",
      flightLabel: "Bradley International",
      featured: false,
    },
    {
      code: "JFK",
      name: "JFK International Airport",
      distance: "~75 miles from New Haven",
      address: "JFK Access Rd, Queens, NY 11430",
      note: "Major international hub with direct flights worldwide. Metro-North and Amtrak connections available from New Haven Union Station.",
      flightUrl: "https://www.jfkairport.com/",
      flightLabel: "JFK International",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-passport-line",
      title: "Emergency Passport",
      description: "For New Haven travelers with urgent departures within 24 hours to 2 weeks. Reserved agency submission with dedicated courier handling.",
      bullets: ["Processing as fast as 24 hours", "Dedicated agent assigned to your case", "Daily status updates"],
      badge: "Fastest",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Fast renewal for New Haven residents with a valid passport issued within the last 15 years at age 16 or older.",
      bullets: ["3-5 day or 10-day options available", "No acceptance clerk visit required", "Secure overnight document return"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-user-add-line",
      title: "New Passport",
      description: "First-time passport application assistance for New Haven residents with step-by-step guidance through the acceptance clerk process.",
      bullets: ["Complete application review before submission", "Acceptance clerk appointment guidance", "3-5 day or 10-day processing options"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alarm-warning-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement service for New Haven residents who have lost their passport or had it stolen.",
      bullets: ["DS-64 form guidance included", "Replacement in 3-5 days or 10 days", "Acceptance clerk visit required"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 in New Haven, with parental consent guidance and acceptance clerk support.",
      bullets: ["Parental consent documentation review", "Both parents present or notarized consent", "3-5 day or 10-day processing available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal name change for New Haven residents.",
      bullets: ["Marriage certificate or court order review", "Name change in 3-5 days or 10 days", "No clerk visit for most renewals"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for New Haven travelers with immediate departure needs." },
    { name: "Passport Renewal", description: "Fast passport renewal for New Haven residents with a recent valid passport." },
    { name: "New Passport", description: "First-time passport application assistance for New Haven residents." },
    { name: "Lost or Stolen Passport", description: "Passport replacement for New Haven residents who have lost their passport." },
    { name: "Child Passport", description: "Passport services for minors under 16 in New Haven." },
    { name: "Name Change", description: "Passport name change service after marriage, divorce, or legal name change in New Haven." },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Photos must be 2\"x2\" with a plain white or off-white background. Many New Haven travelers get rejected at the acceptance clerk because their photos don't meet strict State Department specs.",
    },
    {
      icon: "ri-file-list-3-line",
      title: "Missing or Incorrect Forms",
      description: "Using the wrong form (DS-11 vs DS-82) or missing required fields can cause weeks of delays. We review every form before submission.",
    },
    {
      icon: "ri-calendar-line",
      title: "Waiting Too Long to Start",
      description: "Standard processing takes 8-11 weeks. Many New Haven travelers wait until the last minute, leaving no buffer for problems.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Incorrect Payment Amount",
      description: "Government fees change frequently. Using an outdated check or wrong amount will cause your application to be returned.",
    },
    {
      icon: "ri-shield-line",
      title: "Damaged or Expired ID",
      description: "Your driver's license or state ID must be valid and unexpired. Many applicants don't realize their ID is about to expire until it's too late.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For child passports, both parents must appear or provide notarized consent. Missing documentation is the #1 reason child applications are delayed.",
    },
  ],
  steps: [
    {
      number: "01",
      icon: "ri-file-list-3-line",
      title: "Complete Your Application",
      description: "Fill out your passport application online or over the phone with our New Haven-area team. We review everything for accuracy.",
    },
    {
      number: "02",
      icon: "ri-upload-cloud-line",
      title: "Send Us Your Documents",
      description: "Ship your documents via overnight courier to our secure processing center. We confirm receipt and begin review immediately.",
    },
    {
      number: "03",
      icon: "ri-search-line",
      title: "We Review Everything",
      description: "Our agents verify your forms, photos, and supporting documents meet all State Department requirements — no surprises.",
    },
    {
      number: "04",
      icon: "ri-shield-check-line",
      title: "Courier Submission",
      description: "As a registered U.S. Department of State courier, we submit your application with reserved agency processing slots.",
    },
    {
      number: "05",
      icon: "ri-passport-line",
      title: "Receive Your Passport",
      description: "Your passport is delivered securely to your New Haven address. We provide tracking and daily status updates throughout.",
    },
  ],
  processingOptions: [
    {
      speed: "8-11",
      unit: "weeks",
      label: "Standard Processing",
      tag: "Post Office",
      tagColor: "bg-gray-200 text-gray-600",
      features: [
        "Mail-in application",
        "No expedited handling",
        "No dedicated agent",
        "No phone or email contact for your case",
        "Updates only by physical mail letter",
      ],
      highlight: false,
      muted: true,
    },
    {
      speed: "10",
      unit: "business days",
      label: "Smart Saver",
      tag: "Fast Passport Center",
      tagColor: "bg-green-100 text-green-700",
      features: [
        "Secure overnight shipping included",
        "Document review before submission",
        "Dedicated agent assigned",
        "Daily status updates",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "3-5",
      unit: "business days",
      label: "Expedited Processing",
      tag: "Recommended",
      tagColor: "bg-[#1A365D] text-white",
      features: [
        "Secure overnight shipping included",
        "Document review before submission",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "24",
      unit: "hours",
      label: "Emergency Processing",
      tag: "Life or Death",
      tagColor: "bg-red-100 text-red-600",
      features: [
        "For documented life-or-death emergencies only",
        "Requires proof of emergency travel",
        "Strictly for documented life-or-death emergencies",
      ],
      highlight: false,
      emergency: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Must book weeks in advance", fpc: "No appointment needed — we handle agency access" },
    { feature: "Processing Speed", agency: "1-2 business days (if you get an appointment)", fpc: "As fast as 3-5 days or 24 hours for emergencies" },
    { feature: "Document Review", agency: "No pre-check — one mistake means restart", fpc: "Complete review before submission — we catch errors" },
    { feature: "Travel Proof", agency: "Requires printed itinerary with confirmed travel within 2 weeks", fpc: "Flexible documentation requirements" },
    { feature: "Updates", agency: "No phone or email support — only mail updates", fpc: "Daily status updates via phone and email" },
    { feature: "Guarantee", agency: "No guarantee of appointment or same-day processing", fpc: "Reserved agency slots with predictable processing" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Hartford", route: "/cities/hartford" },
    { name: "New Haven", route: "/cities/new-haven" },
    { name: "Stamford", route: "/cities/stamford" },
    { name: "Bridgeport", route: "/cities/bridgeport" },
    { name: "Waterbury", route: "/cities/waterbury" },
    { name: "Greenwich", route: "/cities/greenwich" },
  ],
  fpcOfficeNote:
    "Fast Passport Center serves New Haven and all of Connecticut as a remote courier service. We do not have a physical office in New Haven, but our secure overnight shipping network ensures your documents reach our processing center quickly and safely.",
  fpcOfficeAddress: "One Boston Place, #2600, Boston, MA 02108",
  fpcOfficeDistance: "Remote courier service — New Haven, CT area",
  serviceAreaIntro:
    "Fast Passport Center provides expedited passport services to all New Haven neighborhoods and surrounding Connecticut communities. From downtown New Haven to West Haven, East Rock to Wooster Square — we serve every New Haven neighborhood with the same fast, reliable processing.",
  nearbyCitiesIntro:
    "We proudly serve New Haven and all surrounding Connecticut communities. Whether you're near Yale campus or a nearby shoreline town, our expedited passport services are available to you with the same speed and reliability.",
  howItWorksIntro:
    "Our New Haven customers follow a simple, proven process to get their passports fast. No agency visits, no waiting in lines, no guessing.",
  mistakesIntro:
    "These are the most common mistakes New Haven travelers make when applying for an expedited passport. Avoiding them saves weeks of delays.",
  testimonialsIntro:
    "New Haven and Connecticut travelers trust Fast Passport Center for their urgent passport needs. Here's what they say about our service.",
  faqIntro:
    "Common questions about expedited passport services in New Haven, CT. If you don't see your question here, call us at 877-253-0084.",
  statePageRoute: "/states/connecticut",
  statePageName: "Connecticut Passport Services",
  dmvWebsite: "https://portal.ct.gov/DMV",
  dmvLabel: "Connecticut DMV — Official Website",
};

export default newHavenConfig;